// ============================================================
//  src/admin.js — Panel de administración
// ============================================================

import { db, auth }              from './firebase.js';
import { getFunctions,
         httpsCallable }         from 'firebase/functions';
import { collection, query,
         where, orderBy,
         onSnapshot, doc,
         updateDoc, addDoc,
         setDoc,
         serverTimestamp,
         getDoc }                from 'firebase/firestore';
import { mostrarToast,
         toggleBoton }           from './ui.js';
import { notificarUsuarioResolucion,
         notificarArchivoDisponible }  from './emails.js';
import { crearNotificacionesArchivo } from './notifications.js';
import { safeUrl }               from './security.js';

const UID_ADMIN = 'YNn8MjySxCPLbIAg6T2Cll386Vk2';

let _cancelarEscucha     = null;
let _cancelarEscuchaMod  = null;
let _cancelarEscuchaMD   = null;
let _solicitudActual     = null;
let _moderacionActual    = null;
let _solicitudMDActual   = null;
let _usuarios            = [];
let _funciones           = null;
let _tabsInicializados   = false;

// ── API pública ───────────────────────────────────────────────

export function esAdmin() {
    return auth.currentUser?.uid === UID_ADMIN;
}

export function abrirPanelAdmin() {
    if (!esAdmin()) return;

    const panel = document.getElementById('panel-admin');
    if (!panel) return;

    panel.classList.add('panel-admin--visible');
    _inicializarTabs();
    _iniciarEscuchaSolicitudes();
    _iniciarEscuchaModeracion();
    _iniciarEscuchaManagedDrive();
}

export function cerrarPanelAdmin() {
    const panel = document.getElementById('panel-admin');
    if (panel) panel.classList.remove('panel-admin--visible');
    _detenerEscucha();
    _detenerEscuchaMod();
    _detenerEscuchaMD();
    cerrarModalRevision();
    cerrarModalModeracion();
    cerrarModalManagedDrive();
    _mostrarTabMod();
    _usuarios = [];
}

export function cerrarModalRevision() {
    const modal = document.getElementById('modal-revision');
    if (modal) modal.classList.remove('modal--visible');
    _solicitudActual = null;
}

// ── Escucha Firestore ─────────────────────────────────────────

export function iniciarEscuchaAdmin() {
    _iniciarEscuchaSolicitudes();
    _iniciarEscuchaModeracion();
    _iniciarEscuchaManagedDrive();
}

export function cerrarModalModeracion() {
    document.getElementById('modal-moderacion')?.classList.remove('modal--visible');
    _moderacionActual = null;
}

function _iniciarEscuchaSolicitudes() {

    _detenerEscucha();

    const q = query(
        collection(db, 'solicitudesConcurso'),
        where('estado', '==', 'pendiente'),
        orderBy('solicitadoEn', 'desc')
    );

    _cancelarEscucha = onSnapshot(q, (snapshot) => {

        const solicitudes = snapshot.docs.map(d => ({
            id: d.id,
            ...d.data(),
            solicitadoEn: d.data().solicitadoEn?.toDate?.() ?? null
        }));

        console.log(`[Admin] ${solicitudes.length} solicitud/es pendiente/s.`);

        _renderizarLista(solicitudes);

    }, (error) => {

        console.error('[Admin] Error en escucha:', error);
        mostrarToast('Error al cargar las solicitudes.', 'error');

    });
}

function _detenerEscucha() {
    if (_cancelarEscucha) { _cancelarEscucha(); _cancelarEscucha = null; }
}

function _detenerEscuchaMod() {
    if (_cancelarEscuchaMod) { _cancelarEscuchaMod(); _cancelarEscuchaMod = null; }
}

// ── Escucha moderación ────────────────────────────────────────

function _iniciarEscuchaModeracion() {
    _detenerEscuchaMod();

    const q = query(
        collection(db, 'moderacionArchivos'),
        where('estado', '==', 'pendiente'),
        orderBy('subidoEn', 'desc')
    );

    _cancelarEscuchaMod = onSnapshot(q, (snapshot) => {
        const items = snapshot.docs.map(d => ({ id: d.id, ...d.data(), subidoEn: d.data().subidoEn?.toDate?.() ?? null }));
        _renderizarListaModeracion(items);
    }, (err) => {
        console.error('[Admin] Error escucha moderación:', err);
    });
}

function _renderizarListaModeracion(items) {
    const contenedor = document.getElementById('admin-lista-moderacion');
    const contador   = document.getElementById('admin-contador-mod');
    if (!contenedor) return;

    contenedor.replaceChildren();
    if (contador) {
        contador.textContent = items.length;
        contador.style.display = items.length > 0 ? 'inline-flex' : 'none';
    }

    if (items.length === 0) {
        const vacio = document.createElement('div');
        vacio.className = 'admin-vacio';
        vacio.innerHTML = '<span class="admin-vacio__icono">✅</span><p class="admin-vacio__texto">Sin archivos pendientes</p>';
        contenedor.appendChild(vacio);
        return;
    }

    items.forEach(item => {
        const card = document.createElement('div');
        card.className = 'admin-solicitud-card';

        const info = document.createElement('div');
        info.className = 'admin-solicitud-card__info';

        const nombre = document.createElement('span');
        nombre.className = 'admin-solicitud-card__nombre';
        nombre.textContent = `${item.tipo === 'res' ? '📊 Resultado' : '🏅 Certificado'} — ${item.concurso} ${item.año}`;

        const meta = document.createElement('span');
        meta.className = 'admin-solicitud-card__meta';
        meta.textContent = `${item.modo} · ${_formatearFecha(item.subidoEn)}`;

        info.append(nombre, meta);

        const boton = document.createElement('button');
        boton.className = 'admin-solicitud-card__btn btn-revisar';
        boton.type = 'button';
        boton.textContent = 'Revisar →';
        boton.addEventListener('click', () => _abrirModalModeracion(item));

        card.append(info, boton);
        contenedor.appendChild(card);
    });
}

function _abrirModalModeracion(item) {
    _moderacionActual = item;

    const modal = document.getElementById('modal-moderacion');
    if (!modal) return;

    _setText('mod-concurso', `${item.concurso} ${item.año} — ${item.modo}`);
    _setText('mod-tipo', item.tipo === 'res' ? '📊 Resultado' : '🏅 Certificado');
    _setText('mod-fecha', _formatearFecha(item.subidoEn));

    modal.classList.add('modal--visible');
}

// ── Renderizado seguro ────────────────────────────────────────

function _renderizarLista(solicitudes) {

    const contenedor = document.getElementById('admin-lista-solicitudes');
    const contador   = document.getElementById('admin-contador');

    if (!contenedor) return;

    contenedor.replaceChildren();

    if (contador) {
        contador.textContent = solicitudes.length;
        contador.style.display = solicitudes.length > 0 ? 'inline-flex' : 'none';
    }

    if (solicitudes.length === 0) {

        const vacio = document.createElement('div');
        vacio.className = 'admin-vacio';

        const icono = document.createElement('span');
        icono.className = 'admin-vacio__icono';
        icono.textContent = '✅';

        const texto = document.createElement('p');
        texto.className = 'admin-vacio__texto';
        texto.textContent = 'No hay solicitudes pendientes';

        vacio.append(icono, texto);
        contenedor.appendChild(vacio);

        return;
    }

    solicitudes.forEach(s => {

        const card = document.createElement('div');
        card.className = 'admin-solicitud-card';
        card.dataset.id = String(s.id || '');

        const info = document.createElement('div');
        info.className = 'admin-solicitud-card__info';

        const nombre = document.createElement('span');
        nombre.className = 'admin-solicitud-card__nombre';
        nombre.textContent = s.nombre || '—';

        const meta = document.createElement('span');
        meta.className = 'admin-solicitud-card__meta';

        const modos = Array.isArray(s.modos) ? s.modos.join(', ') : '—';
        meta.textContent = `${modos} · ${_formatearFecha(s.solicitadoEn)}`;

        info.append(nombre, meta);

        const boton = document.createElement('button');
        boton.className = 'admin-solicitud-card__btn btn-revisar';
        boton.type = 'button';
        boton.textContent = 'Revisar →';

        boton.addEventListener('click', () => {
            _abrirModalRevision(s);
        });

        card.append(info, boton);
        contenedor.appendChild(card);

    });

}

// ── Modal de revisión ─────────────────────────────────────────

function _actualizarLinkWeb(url) {
    const linkWeb = document.getElementById('revision-web-link');
    if (!linkWeb) return;
    const urlSegura = safeUrl(url);
    if (urlSegura) {
        linkWeb.href = urlSegura;
        linkWeb.rel  = 'noopener noreferrer';
        linkWeb.target = '_blank';
        linkWeb.style.display = 'inline';
    } else {
        linkWeb.removeAttribute('href');
        linkWeb.removeAttribute('target');
        linkWeb.removeAttribute('rel');
        linkWeb.style.display = 'none';
    }
}

function _abrirModalRevision(solicitud) {

    _solicitudActual = solicitud;

    const modal = document.getElementById('modal-revision');
    if (!modal) return;

    _setInput('revision-nombre-input',      solicitud.nombre || '');
    _setInput('revision-web-input',         solicitud.webUrl || '');
    _setInput('revision-modos-input',       solicitud.modos?.join(', ')  || '');
    _setInput('revision-bandas-input',      solicitud.bandas?.join(', ') || '');
    _setInput('revision-descripcion-input', solicitud.descripcion || '');
    _setText('revision-fecha',       _formatearFecha(solicitud.solicitadoEn));

    const nombreSol = solicitud.solicitadoNombre || '—';
    const emailSol  = solicitud.solicitadoEmail  || '';
    _setText('revision-solicitante', emailSol ? `${nombreSol} — ${emailSol}` : nombreSol);

    const campoComentario = document.getElementById('revision-comentario');
    if (campoComentario) campoComentario.value = '';

    _actualizarLinkWeb(solicitud.webUrl);

    const webInput = document.getElementById('revision-web-input');
    if (webInput) {
        webInput.oninput = () => _actualizarLinkWeb(webInput.value.trim());
    }

    modal.classList.add('modal--visible');
}

// ── Acciones ──────────────────────────────────────────────────

export async function aprobarSolicitud() {

    if (!_solicitudActual) return;

    const comentario = document.getElementById('revision-comentario')?.value.trim()
        || 'Tu concurso fue aprobado y ya está disponible en el catálogo.';

    const nombreRaw      = document.getElementById('revision-nombre-input')?.value.trim()      || '';
    const webRaw         = document.getElementById('revision-web-input')?.value.trim()         || '';
    const modosRaw       = document.getElementById('revision-modos-input')?.value.trim()       || '';
    const bandasRaw      = document.getElementById('revision-bandas-input')?.value.trim()      || '';
    const descripcionRaw = document.getElementById('revision-descripcion-input')?.value.trim() || '';

    const nombreLimpio = _normalizarEspacios(nombreRaw);

    if (!nombreLimpio) {
        mostrarToast('El nombre no puede estar vacío.', 'error');
        document.getElementById('revision-nombre-input')?.focus();
        return;
    }

    toggleBoton('btn-aprobar', false, 'Aprobando...');

    try {

        const nombreNormalizado  = _normalizarTexto(nombreLimpio);
        const tokensBusqueda     = _generarTokensBusqueda(nombreLimpio);
        const webPrincipalSegura = safeUrl(webRaw);

        const modos  = modosRaw  ? modosRaw.split(',').map(x => x.trim()).filter(Boolean)
                                 : (Array.isArray(_solicitudActual.modos)  ? _solicitudActual.modos  : []);
        const bandas = bandasRaw ? bandasRaw.split(',').map(x => x.trim()).filter(Boolean)
                                 : (Array.isArray(_solicitudActual.bandas) ? _solicitudActual.bandas : []);
        const descripcion = _normalizarEspacios(descripcionRaw);

        await addDoc(collection(db, 'catalogoConcursos'), {
            nombre:            nombreLimpio,
            nombreNormalizado: nombreNormalizado,
            tokensBusqueda:    tokensBusqueda,
            webPrincipal:      webPrincipalSegura || '',
            descripcion:       descripcion,
            modos,
            bandas,
            fuente:            'solicitud',
            creadoEn:          serverTimestamp(),
        });

        await updateDoc(doc(db, 'solicitudesConcurso', _solicitudActual.id), {
            estado:          'aprobada',
            revisadoPor:     auth.currentUser.uid,
            revisadoEn:      serverTimestamp(),
            comentarioAdmin: comentario,
            datosAprobados: {
                nombre:       nombreLimpio,
                webPrincipal: webPrincipalSegura || '',
                descripcion:  descripcion,
                modos,
                bandas,
            },
        });

        const datosUsuario = await _obtenerDatosUsuario(_solicitudActual.solicitadoPor);

        await notificarUsuarioResolucion(
            _solicitudActual,
            datosUsuario,
            'aprobada',
            comentario
        );

        mostrarToast(`"${nombreLimpio}" aprobado.`, 'exito');

        cerrarModalRevision();

    } catch (error) {

        console.error('[Admin] Error aprobar:', error);
        mostrarToast('Error al aprobar.', 'error');

    } finally {

        toggleBoton('btn-aprobar', true, 'Aprobar ✅');

    }
}

export async function rechazarSolicitud() {

    if (!_solicitudActual) return;

    const comentario = document.getElementById('revision-comentario')?.value.trim();

    if (!comentario) {

        mostrarToast('Ingresa un motivo.', 'error');
        document.getElementById('revision-comentario')?.focus();
        return;

    }

    toggleBoton('btn-rechazar', false, 'Rechazando...');

    try {

        await updateDoc(doc(db, 'solicitudesConcurso', _solicitudActual.id), {

            estado: 'rechazada',
            revisadoPor: auth.currentUser.uid,
            revisadoEn: serverTimestamp(),
            comentarioAdmin: comentario

        });

        const datosUsuario = await _obtenerDatosUsuario(_solicitudActual.solicitadoPor);

        await notificarUsuarioResolucion(
            _solicitudActual,
            datosUsuario,
            'rechazada',
            comentario
        );

        mostrarToast('Solicitud rechazada.', 'info');

        cerrarModalRevision();

    } catch (error) {

        console.error('[Admin] Error rechazar:', error);
        mostrarToast('Error al rechazar.', 'error');

    } finally {

        toggleBoton('btn-rechazar', true, 'Rechazar ❌');

    }
}

export async function aprobarModeracion() {
    if (!_moderacionActual) return;

    toggleBoton('btn-mod-aprobar', false, 'Aprobando...');

    try {
        const { id, tipo, concurso, año, modo, archivoId, subidoPor } = _moderacionActual;

        // Obtener URL del archivo
        let archivoUrl = null;
        try {
            const archivoSnap = await getDoc(doc(db, 'editions', _moderacionActual.edicionId));
            // URL está en la subcol files/{archivoId}
            const fileSnap = await getDoc(doc(db, 'editions', _moderacionActual.edicionId, 'files', archivoId));
            if (fileSnap.exists()) archivoUrl = fileSnap.data().fileUrl ?? null;
        } catch { /* continuar sin URL */ }

        // Marcar como aprobado
        await updateDoc(doc(db, 'moderacionArchivos', id), {
            estado:      'aprobado',
            revisadoPor: auth.currentUser.uid,
            revisadoEn:  serverTimestamp(),
        });

        // Crear notificaciones in-app + obtener destinatarios para email
        const destinatarios = await crearNotificacionesArchivo({ tipo, concurso, año, modo, archivoUrl, subidoPor });

        // Enviar emails según preferencias
        const prefKey = tipo === 'res' ? 'resultadosMail' : 'certificadosMail';
        for (const dest of destinatarios) {
            if (dest.email && dest.prefs[prefKey] !== false) {
                notificarArchivoDisponible(
                    { displayName: dest.nombre, email: dest.email },
                    tipo === 'res' ? 'resultado_disponible' : 'certificado_disponible',
                    { concurso, año, modo, archivoUrl }
                ).catch(e => console.warn('[Admin] Email notif:', e));
            }
        }

        mostrarToast('Aprobado. Notificaciones enviadas.', 'exito');
        cerrarModalModeracion();

    } catch (error) {
        console.error('[Admin] Error aprobar moderación:', error);
        mostrarToast('Error al aprobar.', 'error');
    } finally {
        toggleBoton('btn-mod-aprobar', true, 'Aprobar ✅');
    }
}

export async function rechazarModeracion() {
    if (!_moderacionActual) return;

    toggleBoton('btn-mod-rechazar', false, 'Rechazando...');

    try {
        await updateDoc(doc(db, 'moderacionArchivos', _moderacionActual.id), {
            estado:      'rechazado',
            revisadoPor: auth.currentUser.uid,
            revisadoEn:  serverTimestamp(),
        });

        mostrarToast('Archivo rechazado. Sin notificaciones enviadas.', 'info');
        cerrarModalModeracion();

    } catch (error) {
        console.error('[Admin] Error rechazar moderación:', error);
        mostrarToast('Error al rechazar.', 'error');
    } finally {
        toggleBoton('btn-mod-rechazar', true, 'Rechazar ❌');
    }
}

// ── Solicitudes managed_drive ─────────────────────────────────

export function cerrarModalManagedDrive() {
    document.getElementById('modal-managed-drive')?.classList.remove('modal--visible');
    _solicitudMDActual = null;
}

function _detenerEscuchaMD() {
    if (_cancelarEscuchaMD) { _cancelarEscuchaMD(); _cancelarEscuchaMD = null; }
}

function _iniciarEscuchaManagedDrive() {
    _detenerEscuchaMD();

    const q = query(
        collection(db, 'solicitudesManagedDrive'),
        where('estado', '==', 'pendiente')
    );

    _cancelarEscuchaMD = onSnapshot(q, (snapshot) => {
        const items = snapshot.docs.map(d => ({
            id: d.id,
            ...d.data(),
            solicitadoEn: d.data().solicitadoEn?.toDate?.() ?? null,
        }));
        _renderizarListaManagedDrive(items);
    }, (err) => {
        console.error('[Admin] Error escucha managed_drive:', err);
    });
}

function _renderizarListaManagedDrive(items) {
    const contenedor = document.getElementById('admin-lista-managed-drive');
    const contador   = document.getElementById('admin-contador-md');
    if (!contenedor) return;

    contenedor.replaceChildren();
    if (contador) {
        contador.textContent = items.length;
        contador.style.display = items.length > 0 ? 'inline-flex' : 'none';
    }

    if (items.length === 0) {
        const vacio = document.createElement('div');
        vacio.className = 'admin-vacio';
        vacio.innerHTML = '<span class="admin-vacio__icono">✅</span><p class="admin-vacio__texto">Sin solicitudes pendientes</p>';
        contenedor.appendChild(vacio);
        return;
    }

    items.forEach(item => {
        const card = document.createElement('div');
        card.className = 'admin-solicitud-card';

        const info = document.createElement('div');
        info.className = 'admin-solicitud-card__info';

        const nombre = document.createElement('span');
        nombre.className = 'admin-solicitud-card__nombre';
        nombre.textContent = item.displayName || item.uid || '—';

        const meta = document.createElement('span');
        meta.className = 'admin-solicitud-card__meta';
        meta.textContent = `${item.email || '—'} · ${_formatearFecha(item.solicitadoEn)}`;

        info.append(nombre, meta);

        const boton = document.createElement('button');
        boton.className = 'admin-solicitud-card__btn btn-revisar';
        boton.type = 'button';
        boton.textContent = 'Revisar →';
        boton.addEventListener('click', () => _abrirModalManagedDrive(item));

        card.append(info, boton);
        contenedor.appendChild(card);
    });
}

function _abrirModalManagedDrive(solicitud) {
    _solicitudMDActual = solicitud;
    const modal = document.getElementById('modal-managed-drive');
    if (!modal) return;

    _setText('md-nombre', solicitud.displayName || '—');
    _setText('md-email',  solicitud.email        || '—');
    _setText('md-uid',    solicitud.uid           || '—');
    _setText('md-fecha',  _formatearFecha(solicitud.solicitadoEn));

    modal.classList.add('modal--visible');
}

export async function aprobarAccesoManagedDrive() {
    if (!_solicitudMDActual) return;
    toggleBoton('btn-md-aprobar', false, 'Aprobando...');

    const { id, uid } = _solicitudMDActual;

    try {
        const mdRef  = doc(db, 'managedDriveUsers', uid);
        const mdSnap = await getDoc(mdRef);
        const mdData = { enabled: true, quotaBytes: 524288000 };
        if (!mdSnap.exists()) mdData.bytesUsed = 0;
        await setDoc(mdRef, mdData, { merge: true });

        await setDoc(doc(db, 'users', uid), {
            managedDriveAccess:  true,
            storagePreference:   'managed_drive',
            managedDrivePending: false,
        }, { merge: true });

        await updateDoc(doc(db, 'solicitudesManagedDrive', id), {
            estado:     'aprobada',
            aprobadoPor: auth.currentUser.uid,
            aprobadoEn:  serverTimestamp(),
        });

        await addDoc(collection(db, 'notificaciones'), {
            userId:   uid,
            tipo:     'acceso_managed_aprobado',
            mensaje:  'Tu solicitud de acceso al almacenamiento de Contest Manager fue aprobada.',
            leida:    false,
            creadaEn: serverTimestamp(),
        });

        mostrarToast('Acceso aprobado.', 'exito');
        cerrarModalManagedDrive();

    } catch (err) {
        console.error('[Admin] Error aprobar managed_drive:', err);
        mostrarToast('Error al aprobar.', 'error');
    } finally {
        toggleBoton('btn-md-aprobar', true, 'Aprobar ✅');
    }
}

export async function rechazarAccesoManagedDrive() {
    if (!_solicitudMDActual) return;
    toggleBoton('btn-md-rechazar', false, 'Rechazando...');

    try {
        await updateDoc(doc(db, 'solicitudesManagedDrive', _solicitudMDActual.id), {
            estado:      'rechazada',
            rechazadoPor: auth.currentUser.uid,
            rechazadoEn:  serverTimestamp(),
        });

        // Limpiar estado pendiente del usuario para que pueda volver a Mi Drive
        await setDoc(doc(db, 'users', _solicitudMDActual.uid), {
            managedDrivePending: false,
        }, { merge: true });

        mostrarToast('Solicitud rechazada.', 'info');
        cerrarModalManagedDrive();

    } catch (err) {
        console.error('[Admin] Error rechazar managed_drive:', err);
        mostrarToast('Error al rechazar.', 'error');
    } finally {
        toggleBoton('btn-md-rechazar', true, 'Rechazar ❌');
    }
}

// ── Helpers ───────────────────────────────────────────────────

async function _obtenerDatosUsuario(uid) {

    try {

        const snap = await getDoc(doc(db, 'users', uid));

        if (snap.exists()) {

            const d = snap.data();

            return {
                displayName: d.nombre || d.displayName || 'Operador',
                email: d.email || ''
            };

        }

    } catch (e) {

        console.warn('[Admin] usuario no encontrado');

    }

    return { displayName: 'Operador', email: '' };
}

function _setText(id, texto) {
    const el = document.getElementById(id);
    if (el) el.textContent = texto;
}

function _setInput(id, valor) {
    const el = document.getElementById(id);
    if (el) el.value = valor;
}

function _formatearFecha(fecha) {

    if (!fecha) return '—';

    return fecha.toLocaleDateString('es-UY', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

function _normalizarEspacios(texto) {
    return String(texto || '').replace(/\s+/g, ' ').trim();
}

function _normalizarTexto(texto) {

    return _normalizarEspacios(texto)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

}

function _tokenizarTexto(texto) {

    return _normalizarTexto(texto)
        .replace(/[^a-z0-9+/.-]+/g, ' ')
        .split(' ')
        .map(t => t.trim())
        .filter(Boolean);

}

function _generarTokensBusqueda(nombre) {

    const tokens = new Set();
    const base = _tokenizarTexto(nombre);

    base.forEach(t => tokens.add(t));

    const completo = _normalizarTexto(nombre);
    if (completo) tokens.add(completo);

    return Array.from(tokens);

}

// ── Panel Admin — Sección Usuarios ───────────────────────────

function _inicializarTabs() {
    if (_tabsInicializados) return;
    _tabsInicializados = true;

    document.getElementById('admin-tab-moderacion')
        ?.addEventListener('click', _mostrarTabMod);
    document.getElementById('admin-tab-usuarios')
        ?.addEventListener('click', _mostrarTabUsuarios);
    document.getElementById('admin-buscador-usuarios')
        ?.addEventListener('input', e => _filtrarUsuarios(e.target.value.trim()));
}

function _mostrarTabMod() {
    const m = document.getElementById('admin-cuerpo-moderacion');
    const u = document.getElementById('admin-cuerpo-usuarios');
    if (m) m.style.removeProperty('display');
    if (u) u.style.display = 'none';
    document.getElementById('admin-tab-moderacion')?.classList.add('admin-tab--activa');
    document.getElementById('admin-tab-usuarios')?.classList.remove('admin-tab--activa');
}

function _mostrarTabUsuarios() {
    const m = document.getElementById('admin-cuerpo-moderacion');
    const u = document.getElementById('admin-cuerpo-usuarios');
    if (m) m.style.display = 'none';
    if (u) u.style.removeProperty('display');
    document.getElementById('admin-tab-moderacion')?.classList.remove('admin-tab--activa');
    document.getElementById('admin-tab-usuarios')?.classList.add('admin-tab--activa');
    if (_usuarios.length === 0) cargarUsuariosAdmin();
}

export async function cargarUsuariosAdmin() {
    if (!esAdmin()) return;

    const wrapper = document.getElementById('admin-tabla-usuarios-wrapper');
    if (!wrapper) return;

    wrapper.replaceChildren();
    const spinner = document.createElement('div');
    spinner.className = 'admin-vacio';
    spinner.innerHTML = '<span class="admin-vacio__icono">⏳</span><p class="admin-vacio__texto">Cargando usuarios…</p>';
    wrapper.appendChild(spinner);

    try {
        if (!_funciones) _funciones = getFunctions(undefined, 'us-central1');
        const fn     = httpsCallable(_funciones, 'adminGetUsers');
        const result = await fn();
        _usuarios    = result.data?.users ?? [];
        _renderizarTablaUsuarios(_usuarios);
    } catch (err) {
        console.error('[Admin] Error cargando usuarios:', err);
        wrapper.replaceChildren();
        const error = document.createElement('div');
        error.className = 'admin-vacio';
        error.innerHTML = '<span class="admin-vacio__icono">⚠️</span><p class="admin-vacio__texto">Error al cargar usuarios.</p>';
        wrapper.appendChild(error);
        mostrarToast('Error al cargar usuarios.', 'error');
    }
}

function _filtrarUsuarios(query) {
    if (!query) { _renderizarTablaUsuarios(_usuarios); return; }
    const q = query.toLowerCase();
    const filtrados = _usuarios.filter(u =>
        (u.indicativoOperador || '').toLowerCase().includes(q) ||
        (u.nombre             || '').toLowerCase().includes(q) ||
        (u.email              || '').toLowerCase().includes(q) ||
        (u.correoElectronico  || '').toLowerCase().includes(q)
    );
    _renderizarTablaUsuarios(filtrados);
}

function _renderizarTablaUsuarios(usuarios) {
    const wrapper = document.getElementById('admin-tabla-usuarios-wrapper');
    if (!wrapper) return;

    wrapper.replaceChildren();

    if (usuarios.length === 0) {
        const vacio = document.createElement('div');
        vacio.className = 'admin-vacio';
        const icono = document.createElement('span');
        icono.className = 'admin-vacio__icono';
        icono.textContent = '👤';
        const texto = document.createElement('p');
        texto.className = 'admin-vacio__texto';
        texto.textContent = _usuarios.length === 0
            ? 'Sin usuarios cargados.'
            : 'Sin resultados para esa búsqueda.';
        vacio.append(icono, texto);
        wrapper.appendChild(vacio);
        return;
    }

    const scroll = document.createElement('div');
    scroll.className = 'admin-tabla-scroll';

    const tabla = document.createElement('table');
    tabla.className = 'admin-tabla-usuarios';

    const thead = document.createElement('thead');
    const trH   = document.createElement('tr');
    ['Indicativo', 'Nombre', 'Email', 'UID', 'Storage', 'CM', 'Drive', ''].forEach(col => {
        const th = document.createElement('th');
        th.textContent = col;
        trH.appendChild(th);
    });
    thead.appendChild(trH);
    tabla.appendChild(thead);

    const tbody = document.createElement('tbody');
    usuarios.forEach(u => {
        const tr = document.createElement('tr');

        [
            u.indicativoOperador              || '—',
            u.nombre                          || '—',
            u.email || u.correoElectronico    || '—',
            u.uid ? u.uid.slice(0, 8) + '…'  : '—',
            u.storagePreference               || '—',
            u.managedDriveAccess != null ? (u.managedDriveAccess ? '✓' : '✗') : '—',
            u.driveAutorizado     != null ? (u.driveAutorizado    ? '✓' : '✗') : '—',
        ].forEach(val => {
            const td = document.createElement('td');
            td.textContent = val;
            tr.appendChild(td);
        });

        const tdBtn = document.createElement('td');
        const btn   = document.createElement('button');
        btn.className   = 'admin-solicitud-card__btn btn-revisar';
        btn.type        = 'button';
        btn.textContent = 'Ver detalle →';
        btn.addEventListener('click', () => _abrirDetalleUsuario(u));
        tdBtn.appendChild(btn);
        tr.appendChild(tdBtn);

        tbody.appendChild(tr);
    });

    tabla.appendChild(tbody);
    scroll.appendChild(tabla);
    wrapper.appendChild(scroll);
}

function _abrirDetalleUsuario(usuario) {
    const panel = document.getElementById('admin-usuario-detalle');
    if (!panel) return;

    panel.replaceChildren();

    if (usuario.avatar) {
        const img = document.createElement('img');
        img.src            = usuario.avatar;
        img.alt            = usuario.nombre || '';
        img.referrerPolicy = 'no-referrer';
        img.className      = 'admin-usuario-detalle__avatar';
        panel.appendChild(img);
    }

    const fechaReg = _tsAFecha(usuario.fechaRegistro);

    const campos = [
        ['UID',              usuario.uid],
        ['Indicativo',       usuario.indicativoOperador],
        ['Nombre',           usuario.nombre],
        ['Email',            usuario.email],
        ['Correo contacto',  usuario.correoElectronico],
        ['Storage',          usuario.storagePreference],
        ['Acceso CM',        usuario.managedDriveAccess != null ? (usuario.managedDriveAccess ? 'Sí' : 'No') : null],
        ['Drive autorizado', usuario.driveAutorizado    != null ? (usuario.driveAutorizado    ? 'Sí' : 'No') : null],
        ['Registro',         fechaReg ? _formatearFecha(fechaReg) : null],
    ];

    const dl = document.createElement('dl');
    dl.className = 'admin-usuario-detalle__dl';
    campos.forEach(([etiqueta, valor]) => {
        if (valor == null || valor === '') return;
        const dt = document.createElement('dt');
        dt.textContent = etiqueta;
        const dd = document.createElement('dd');
        dd.textContent = String(valor);
        dl.append(dt, dd);
    });
    panel.appendChild(dl);

    const btnCerrar = document.createElement('button');
    btnCerrar.className   = 'btn-cancelar';
    btnCerrar.type        = 'button';
    btnCerrar.textContent = 'Cerrar';
    btnCerrar.style.marginTop = '.5rem';
    btnCerrar.addEventListener('click', () => { panel.style.display = 'none'; });
    panel.appendChild(btnCerrar);

    panel.style.removeProperty('display');
    panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

function _tsAFecha(ts) {
    if (!ts) return null;
    if (ts._seconds != null) return new Date(ts._seconds * 1000);
    if (ts.seconds  != null) return new Date(ts.seconds  * 1000);
    return null;
}

